import { __decorate } from "tslib";
import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { action, computed, isArrayLike, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import isString from 'lodash/isString';
import isPlainObject from 'lodash/isPlainObject';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import { BigNumber } from 'bignumber.js';
import { FormatNumberFuncOptions } from 'choerodon-ui/dataset/data-set/Field';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import { TextField } from '../text-field/TextField';
import autobind from '../_util/autobind';
import keepRunning from '../_util/keepRunning';
import Icon from '../icon';
import { getNearStepValues, MAX_SAFE_INTEGER, MIN_SAFE_INTEGER, parseNumber, plus, bigNumberToFixed, parseBigNumber } from './utils';
import isEmpty from '../_util/isEmpty';
import { $l } from '../locale-context';
import { FieldType } from '../data-set/enum';
import defaultFormatNumber from '../formatter/formatNumber';
import defaultFormatBigNumber from '../formatter/formatBigNumber';
import localeContext from '../locale-context/LocaleContext';
import { getNumberFormatOptions, getNumberFormatter, getBigNumberFormatter, getBigNumberFormatOptions } from '../field/utils';
// BigNumber: https://mikemcl.github.io/bignumber.js/
function getCurrentValidValue(value, stringMode) {
    if (stringMode) {
        const valueBig = new BigNumber(value.replace(/\.$/, ''));
        const validateValue = bigNumberToFixed(valueBig, undefined, '0');
        return validateValue;
    }
    return Number(value.replace(/\.$/, '')) || 0;
}
function getStepUpGenerator() {
    return this.stepGenerator(true);
}
function getStepDownGenerator() {
    return this.stepGenerator(false);
}
function run(value) {
    const { element } = this;
    if (element) {
        element.value = String(value);
    }
}
function enabled() {
    return Boolean(this.getProp('step'));
}
const stepUpKeyboardProps = {
    getStepGenerator: getStepUpGenerator,
    run,
    validator(target) {
        return target === this.element;
    },
    enabled,
};
const stepDownKeyboardProps = {
    getStepGenerator: getStepDownGenerator,
    run,
    validator(target) {
        return target === this.element;
    },
    enabled,
};
const stepUpMouseProps = {
    getStepGenerator: getStepUpGenerator,
    run,
    validator(target) {
        return target === this.plusElement;
    },
};
const stepDownMouseProps = {
    getStepGenerator: getStepDownGenerator,
    run,
    validator(target) {
        return target === this.minusElement;
    },
};
export { FormatNumberFuncOptions };
export class NumberField extends TextField {
    get lang() {
        const { lang } = this.observableProps;
        if (lang) {
            return lang;
        }
        const { dataSet } = this;
        if (dataSet && dataSet.lang) {
            return dataSet.lang;
        }
        const { numberFormatLanguage, locale } = localeContext;
        if (numberFormatLanguage) {
            return numberFormatLanguage;
        }
        return locale.lang;
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('NumberField', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
        };
    }
    get allowDecimal() {
        const { min, nonStrictStep, stringMode } = this;
        const precision = this.getProp('precision');
        if (precision === 0) {
            return false;
        }
        // 非严格步距下允许输入小数
        if (nonStrictStep) {
            return true;
        }
        const step = this.getProp('step');
        if (stringMode) {
            return !step
                || !new BigNumber(step).modulo(1).isEqualTo(0)
                || (!!min && !new BigNumber(min).modulo(1).isEqualTo(0));
        }
        return !step || step % 1 !== 0 || (!!min && min % 1 !== 0);
    }
    get nonStrictStep() {
        const nonStrictStep = this.getProp('nonStrictStep');
        if (nonStrictStep !== undefined) {
            return nonStrictStep;
        }
        const numberFieldNonStrictStep = this.getContextConfig('numberFieldNonStrictStep');
        if (numberFieldNonStrictStep !== undefined) {
            return numberFieldNonStrictStep;
        }
        return false;
    }
    get allowNegative() {
        const { min } = this;
        return isNil(min) || min < 0;
    }
    get min() {
        return this.getLimit('min');
    }
    get max() {
        return this.getLimit('max');
    }
    get value() {
        const { value } = this.observableProps;
        if (isArrayLike(value)) {
            return value;
        }
        const { range } = this;
        if (isArrayLike(range)) {
            if (isPlainObject(value)) {
                const [start, end] = range;
                return {
                    [start]: value[start],
                    [end]: value[end],
                };
            }
        }
        return value;
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    get keyboard() {
        if ('keyboard' in this.props) {
            return this.props.keyboard;
        }
        return this.getContextConfig('numberFieldKeyboard') !== false;
    }
    get stringMode() {
        if ('stringMode' in this.props) {
            return this.props.stringMode;
        }
        return !!this.field && !!this.record && this.field.get('type', this.record) === FieldType.bigNumber;
    }
    savePlusRef(ref) {
        this.plusElement = ref;
    }
    saveMinusRef(ref) {
        this.minusElement = ref;
    }
    isLowerRange(value1, value2) {
        if (this.stringMode) {
            return new BigNumber(value1).isLessThan(value2);
        }
        return value1 < value2;
    }
    getFieldType() {
        return FieldType.number;
    }
    getLimit(type) {
        const { record } = this;
        const limit = this.getProp(type);
        if (record && isString(limit)) {
            const num = record.get(limit);
            if (num !== undefined) {
                return num;
            }
        }
        return limit;
    }
    getValidatorProp(key) {
        if (['maxLength', 'minLength'].includes(key)) {
            return;
        }
        switch (key) {
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            case 'nonStrictStep':
                return this.nonStrictStep;
            case 'stringMode':
                return this.stringMode;
            default:
                return super.getValidatorProp(key);
        }
    }
    getInnerSpanButton() {
        const { prefixCls, range } = this;
        const { longPressPlus } = this.props;
        const step = this.getProp('step');
        if (step && !range && !this.readOnly && !this.disabled) {
            const plusIconProps = {
                ref: this.savePlusRef,
                key: 'plus',
                className: `${prefixCls}-plus`,
                onMouseDown: longPressPlus ? this.handlePlus : this.handleOncePlus,
                onTouchStart: this.handleOncePlus,
            };
            const minIconProps = {
                ref: this.saveMinusRef,
                key: 'minus',
                className: `${prefixCls}-minus`,
                onMouseDown: longPressPlus ? this.handleMinus : this.handleOnceMinus,
                onTouchStart: this.handleOnceMinus,
            };
            return this.wrapperInnerSpanButton(React.createElement("div", null,
                React.createElement("div", Object.assign({}, plusIconProps),
                    React.createElement(Icon, { type: "keyboard_arrow_up" })),
                React.createElement("div", Object.assign({}, minIconProps),
                    React.createElement(Icon, { type: "keyboard_arrow_down" }))));
        }
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        const suffix = this.getSuffix();
        const button = this.getInnerSpanButton();
        return super.getWrapperClassNames({
            [`${prefixCls}-step-suffix`]: button && isValidElement(suffix),
            ...args,
        });
    }
    handleEnterDown(e) {
        if (this.multiple && this.range && this.text) {
            this.prepareSetValue(this.text);
        }
        super.handleEnterDown(e);
        if (this.multiple && this.range) {
            this.setRangeTarget(0);
            this.beginRange();
        }
    }
    handleKeyDown(e) {
        if (!this.disabled && !this.readOnly && this.keyboard) {
            switch (e.keyCode) {
                case KeyCode.UP:
                    this.handleKeyDownUp(e);
                    break;
                case KeyCode.DOWN:
                    this.handleKeyDownDown(e);
                    break;
                default:
            }
        }
        super.handleKeyDown(e);
    }
    handleKeyDownUp(value) {
        this.afterStep(value);
    }
    handleKeyDownDown(value) {
        this.afterStep(value);
    }
    handlePlus(value) {
        this.afterStep(value);
    }
    handleMinus(value) {
        this.afterStep(value);
    }
    handleOncePlus() {
        this.step(true);
    }
    handleOnceMinus() {
        this.step(false);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'nonStrictStep',
            'formatter',
            'formatterOptions',
            'longPressPlus',
            'precision',
            'numberGrouping',
            'maxLength',
            'minLength',
            'keyboard',
            'stringMode',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        delete otherProps.maxLength;
        otherProps.max = this.max;
        otherProps.min = this.min;
        return otherProps;
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            max: props.max,
            min: props.min,
            precision: props.precision,
            nonStrictStep: props.nonStrictStep,
        };
    }
    stepGenerator(isPlus) {
        const { min, max, nonStrictStep, stringMode } = this;
        const step = defaultTo(this.getProp('step'), 1);
        const value = this.getCurrentInputValue();
        const currentValue = getCurrentValidValue(String(value), stringMode);
        return (function* (newValue) {
            while (true) {
                const nearStep = getNearStepValues(newValue, step, min, max, stringMode);
                if (nonStrictStep === false && nearStep) {
                    switch (nearStep.length) {
                        case 1:
                            newValue = nearStep[0];
                            break;
                        case 2:
                            newValue = nearStep[isPlus ? 1 : 0];
                            break;
                        default:
                    }
                }
                else {
                    let nextValue;
                    if (stringMode) {
                        const newValueBig = new BigNumber(newValue);
                        const nextValueBig = isPlus ? newValueBig.plus(step) : newValueBig.minus(step);
                        nextValue = bigNumberToFixed(nextValueBig, undefined, '0');
                    }
                    else {
                        nextValue = plus(Number(newValue), (isPlus ? Number(step) : -Number(step)));
                    }
                    if (min && ((stringMode && new BigNumber(nextValue).isLessThan(min)) || (!stringMode && nextValue < min))) {
                        newValue = min;
                    }
                    else if (max && ((stringMode && new BigNumber(nextValue).isGreaterThan(max)) || (!stringMode && nextValue > max))) {
                        const nearMaxStep = getNearStepValues(max, step, min, max, stringMode);
                        if (nearMaxStep) {
                            newValue = nearMaxStep[0];
                        }
                        else {
                            newValue = max;
                        }
                    }
                    else {
                        newValue = nextValue;
                    }
                }
                yield newValue;
            }
        })(currentValue);
    }
    getCurrentInputValue() {
        let value;
        if (this.text) {
            value = this.text;
        }
        else if (this.range && !isNil(this.rangeTarget) && this.rangeValue) {
            value = this.rangeValue[this.rangeTarget];
        }
        else {
            value = this.getValue();
        }
        return value;
    }
    step(isPlus) {
        this.afterStep(this.stepGenerator(isPlus).next().value);
    }
    afterStep(newValue) {
        if (this.multiple) {
            this.setText(String(newValue));
        }
        else {
            this.prepareSetValue(newValue);
        }
    }
    prepareSetValue(value) {
        if (isNaN(value) || isEmpty(value)) {
            super.prepareSetValue(null);
        }
        else if (this.stringMode) {
            const valueBig = new BigNumber(value);
            super.prepareSetValue(parseBigNumber(valueBig, this.getProp('precision'), null));
        }
        else {
            super.prepareSetValue(parseNumber(value, this.getProp('precision')));
        }
    }
    restrictInput(value) {
        if (value) {
            let restrict = '0-9';
            if (this.allowDecimal) {
                restrict += '.';
            }
            const isNegative = this.allowNegative && /^-/.test(value);
            value = super.restrictInput(value.replace(new RegExp(`[^${restrict}]+`, 'g'), ''));
            const values = value.split('.');
            if (values.length > 2) {
                value = `${values.shift()}.${values.join('')}`;
            }
            if (isNegative) {
                value = `-${value}`;
            }
        }
        return value;
    }
    getFormatOptions(value) {
        return getNumberFormatOptions((name) => this.getProp(name), () => this.getValue(), value, this.lang);
    }
    getFormatter() {
        return this.getProp('formatter') || getNumberFormatter();
    }
    getBigNumberFormatValue(value, numberField) {
        if (isNil(value)) {
            return value;
        }
        const formatOptions = getBigNumberFormatOptions((name) => this.getProp(name), () => this.getValue(), String(value), this.lang, numberField ? 'number-field' : 'currency');
        const formatter = this.getProp('formatter') || getBigNumberFormatter();
        return formatter(value, formatOptions.lang, formatOptions.options, numberField ? 'number-field' : 'currency');
    }
    processText(value) {
        if (this.stringMode) {
            return this.getBigNumberFormatValue(value, true);
        }
        const formatOptions = this.getFormatOptions(Number(value));
        return this.getFormatter()(value, formatOptions.lang, formatOptions.options);
    }
    renderLengthInfo() {
        return undefined;
    }
}
NumberField.displayName = 'NumberField';
NumberField.propTypes = {
    /**
     * 最小值
     */
    min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     * 最大值
     */
    max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     * 步距
     */
    step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     * 非严格步距
     */
    nonStrictStep: PropTypes.bool,
    /**
     * 格式器
     */
    formatter: PropTypes.func,
    /**
     * 格式器参数
     */
    longPressPlus: PropTypes.bool,
    /**
     * 是否开启长按步距增加
     */
    formatterOptions: PropTypes.object,
    /**
     * 是否启用UP DOWN键盘事件
     */
    keyboard: PropTypes.bool,
    ...TextField.propTypes,
};
NumberField.defaultProps = {
    ...TextField.defaultProps,
    suffixCls: 'input-number',
    longPressPlus: true,
    max: MAX_SAFE_INTEGER,
    min: MIN_SAFE_INTEGER,
};
NumberField.format = defaultFormatNumber;
NumberField.bigNumberFormat = defaultFormatBigNumber;
__decorate([
    computed
], NumberField.prototype, "lang", null);
__decorate([
    computed
], NumberField.prototype, "allowDecimal", null);
__decorate([
    computed
], NumberField.prototype, "nonStrictStep", null);
__decorate([
    computed
], NumberField.prototype, "allowNegative", null);
__decorate([
    computed
], NumberField.prototype, "min", null);
__decorate([
    computed
], NumberField.prototype, "max", null);
__decorate([
    computed
], NumberField.prototype, "value", null);
__decorate([
    autobind
], NumberField.prototype, "savePlusRef", null);
__decorate([
    autobind
], NumberField.prototype, "saveMinusRef", null);
__decorate([
    action
], NumberField.prototype, "handleEnterDown", null);
__decorate([
    autobind
], NumberField.prototype, "handleKeyDown", null);
__decorate([
    keepRunning(stepUpKeyboardProps)
], NumberField.prototype, "handleKeyDownUp", null);
__decorate([
    keepRunning(stepDownKeyboardProps)
], NumberField.prototype, "handleKeyDownDown", null);
__decorate([
    keepRunning(stepUpMouseProps)
], NumberField.prototype, "handlePlus", null);
__decorate([
    keepRunning(stepDownMouseProps)
], NumberField.prototype, "handleMinus", null);
__decorate([
    autobind
], NumberField.prototype, "handleOncePlus", null);
__decorate([
    autobind
], NumberField.prototype, "handleOnceMinus", null);
let ObserverNumberField = class ObserverNumberField extends NumberField {
};
ObserverNumberField.defaultProps = NumberField.defaultProps;
ObserverNumberField.format = defaultFormatNumber;
ObserverNumberField.bigNumberFormat = defaultFormatBigNumber;
ObserverNumberField = __decorate([
    observer
], ObserverNumberField);
export default ObserverNumberField;
